import { toPng } from 'html-to-image';
import { useMutation } from '@tanstack/react-query';
import type {
  ReportIssueAttachment,
  ReportIssueKind,
  ReportIssuePriority,
} from '@ampersandbrands/ui';
import { COCKPIT_DEPARTMENTS, findTool, isLaunched } from '@/data/tools.config';
import { activeToolForPath } from '@/lib/toolRouting';
import type { ReportPageStateEntry } from '@/lib/reportPageState';
import { apiPost, API_BASE } from './api';

export type SupportPriority = ReportIssuePriority;

export type SupportAttachment = ReportIssueAttachment;

/**
 * The auto-captured page screenshot. Same shape as any other attachment; the
 * shared dialog seeds it as the first entry of its attachment list.
 */
export type SupportScreenshot = ReportIssueAttachment;

export type SupportIssueInput = {
  product: 'cockpit';
  kind: ReportIssueKind;
  tool?: string;
  summary: string;
  description: string;
  /**
   * The description as HTML from the shared dialog's rich-text editor. The api
   * converts it to Jira's document format. Inline images carry a
   * `data-attachment-index` attribute pointing at the matching entry of
   * `attachments`. Optional so an older client that only sends plain
   * `description` keeps working.
   */
  description_html?: string;
  priority: SupportPriority;
  route?: string;
  url?: string;
  page_title?: string;
  /**
   * Human-readable page state, one entry per filter/setting the operator has
   * actually applied, in the same words the page itself shows (ISD-357). When
   * the calling page has published entries via `useReportPageState`, the api
   * prefers these over parsing `url`'s raw query string. Omitted entirely
   * when the page has published nothing, so the api falls back to its
   * existing URL-parsing behavior.
   */
  page_state?: ReportPageStateEntry[];
  /**
   * Kept alongside `attachments` while the api still accepts it, so nothing
   * regresses part-way through a deploy. It repeats the first image of
   * `attachments`.
   */
  screenshot?: SupportScreenshot;
  /** Every file the reporter attached, up to the api's limit of 10. */
  attachments?: SupportAttachment[];
  client_context?: {
    timestamp?: string;
    user_agent?: string;
  };
};

export type SupportIssueResponse = {
  ticket: {
    key: string;
    id?: string;
    url: string;
    warnings: string[];
  };
};

export const COCKPIT_TOOL_OPTIONS = COCKPIT_DEPARTMENTS.flatMap((dept) =>
  dept.tools
    .filter(isLaunched)
    .map((tool) => `${dept.name} - ${tool.name}`),
);

export function cockpitToolLabelForPath(pathname: string): string | undefined {
  const active = activeToolForPath(pathname);
  if (!active) return undefined;
  const found = findTool(active.deptId, active.toolId);
  return found ? `${found.dept.name} - ${found.tool.name}` : undefined;
}

function screenshotFilename(tool?: string): string {
  const label = (tool ?? 'cockpit').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${label || 'cockpit'}-${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
}

export async function captureCockpitScreenshot(tool?: string): Promise<SupportScreenshot | null> {
  const node = document.querySelector<HTMLElement>('[data-cockpit-main]') ?? document.body;
  try {
    const dataUrl = await toPng(node, {
      cacheBust: true,
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      backgroundColor: document.documentElement.classList.contains('dark') ? '#171714' : '#f6f1e7',
    });
    const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');
    if (!base64) return null;
    return {
      filename: screenshotFilename(tool),
      content_type: 'image/png',
      base64,
    };
  } catch {
    return null;
  }
}

export function useCreateSupportIssue() {
  return useMutation<SupportIssueResponse, Error, SupportIssueInput>({
    mutationFn: (input) => apiPost<SupportIssueResponse>(`${API_BASE}/v1/core/support/issue`, input),
  });
}
